import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Switch,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import useSelectedCompany from '../../../../../components/hooks/useSelectedCompany';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

import { 
  CreateCompanyConfigBIZcore,
  DeleteCompanyConfigBIZcore,
  GetCompanyConfigBIZcore,
  ToggleCompanyConfigBIZcore,
} from '../../../../../redux/actions/business/core/companyConfigActions';

import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import CompanyConfigModal from './CompanyConfigModal';

import global from "../../../../../global.scss";

const CompanyConfigPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core"]);
  const companyId = useSelectedCompany();
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [companyConfigModalOpen, setCompanyConfigModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);
    
  const [companyConfigList, setCompanyConfigList] = useState([]);
  const [companyConfigTotal, setCompanyConfigTotal] = useState(0);

  const [companyConfigMenuOpen, setCompanyConfigMenuOpen] = useState(false);
  const [companyConfigMenuAnchor, setCompanyConfigMenuAnchor] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();
  
  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "json",
      sort: null,
    },
    {
      label: "Description",
      sort: "description",
    },
    {
      label: "Active",
      sort: "is_active",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    },
    {
      label: "",
      sort: null,
    },
  ];


  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  
  useEffect(() => {
    handleGetCompanyConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      console.log(state)
      formatCompanyConfigList(state.companyConfigList.records || []);
      setCompanyConfigTotal(state.companyConfigList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatCompanyConfigList = (list) => {
    setCompanyConfigList(list);
  };

  async function handleGetCompanyConfig(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    await dispatch(GetCompanyConfigBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  async function handleCreateCompanyConfig(){
    try {
      dispatch(CreateCompanyConfigBIZcore(companyId, {}, setLoading)).then(() => {
        handleGetCompanyConfig(setLoading);
      });
    } catch (err) {
      console.log(err);
    }
  }

  async function handleToggleCompanyConfig(id){
    try {
      dispatch(ToggleCompanyConfigBIZcore(companyId, id, setLoading)).then(() => {
        handleGetCompanyConfig(setLoading);
      });
    } catch (err) {
      console.log(err);
    }
  }

  async function handleDelete(){
    setDeleteConfirmationModalOpen(false);
    dispatch(DeleteCompanyConfigBIZcore(companyId, deleteId, setDeleting)).then(() => {
      handleGetCompanyConfig().then(() => {
        setDeleteId(null);
      })
    });
  }

    
  return (
    <Box>

      <CompanyConfigModal 
        open={companyConfigModalOpen}
        setOpen={setCompanyConfigModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetCompanyConfig}
        />

      <ConfirmationModal 
        open={deleteConfirmationModalOpen}
        setOpen={setDeleteConfirmationModalOpen}
        title={"Delete " + modalTitle}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
        />

      <TopBar
        title={"Company Config"}
        button1Text={"Create new company config"}
        button1OnClick={() => {
          handleCreateCompanyConfig();
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          companyConfigList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No config found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                companyConfigList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        {row.id || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>{"JSON" || <NA />}</TableCell>
                    <TableCell>{row.description || <NA />}</TableCell> 
                    <TableCell>
                      <Switch
                        checked={row.is_active}
                        onChange={(e) => {
                          handleToggleCompanyConfig(row.id);
                        }}
                        />
                    </TableCell>   
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        row.id === deleteId && deleting
                        ?
                        <CircularProgress size={26} sx={{ margin: "7px" }}/>
                        :
                        <IconButton
                          onClick={(e) => {
                            setCompanyConfigMenuAnchor(e.currentTarget);
                            setCompanyConfigMenuOpen(true);
                            setUpdate(row);
                            setModalTitle(row.id || "");
                            setDeleteId(row.id);
                          }}>
                          <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                        </IconButton>
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={companyConfigTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={companyConfigMenuOpen}
        anchorEl={companyConfigMenuAnchor}
        onClose={() => setCompanyConfigMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setCompanyConfigMenuOpen(false);
            setCompanyConfigModalOpen(!companyConfigModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
        {
          update?.is_active === false
          &&
          <MenuItem
            onClick={() => {
              setCompanyConfigMenuOpen(false);
              setDeleteConfirmationModalOpen(true);
            }}>
            <Box
              className="flexCenter_Row"
              gap={"10px"}>
              <DeleteIcon />
              Delete
            </Box>
          </MenuItem>
        }
      </Menu>
    </Box>
  )
}

export default CompanyConfigPage;